import {
  Avatar,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from '@material-ui/core';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import clsx from 'clsx';

// icon
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';

// local imports
import { useStateCallback } from 'app/hooks';
import { SystemUserService } from 'app/services';
import { updateUserProfile } from 'app/auth/store/userSlice';
import { roleNameMapper } from 'app/utils/mapper';

// styles
const useStyles = makeStyles(theme => ({
  avatarWrapper: {
    position: 'relative',
    overflow: 'none',
    display: 'block',
    '&:hover': {
      '& $deleteBtn': {
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        display: 'flex'
      }
    }
  },
  avatar: {
    border: `4px solid ${theme.palette.background.default}`,
    objectFit: 'cover',
    position: 'absolute'
  },
  deleteBtn: {
    display: 'none',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: '0',
    left: '0',
    zIndex: 5
  }
}));

// animation variants
const photo = {
  show: { opacity: 1, x: 0, scale: 1, transition: { delay: 0.2 } },
  hidden: { scale: 0, opacity: 0 }
};
const text = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { delay: 0.2 } }
};

const SystemUserCard = ({ handleRefresh, user }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useStateCallback(false);

  // variables
  const userRoles = useSelector(({ auth: { user } }) => user.roles);
  const profileData = useSelector(({ auth }) => auth?.user?.data);
  const userId = user?.id;
  const profileId = profileData?.id;
  const userRoleId = user?.role_id;
  const systemUserRole = roleNameMapper(userRoleId, userRoles);
  const isItMe = profileId === userId;

  const apiURL = process.env.REACT_APP_API;
  const userPhotoSrc = user?.photo_url
    ? `${apiURL}/${user?.photo_url}`
    : 'assets/images/avatars/profile.jpg';

  // ***  Delete Users Profile Photo ***
  const handleDeletePhoto = useCallback(async () => {
    setLoading(true, async () => {
      try {
        await SystemUserService.deleteUserPhoto(userId);
        handleRefresh();

        // update profile photo if the user is the profile itself
        isItMe && dispatch(updateUserProfile({ photo_url: null }));
      } catch (err) {
        console.log('Error -> ', err);
      } finally {
        setLoading(false);
      }
    });
  }, [setLoading, userId, handleRefresh, isItMe, dispatch]);

  return (
    <>
      <Grid container spacing={2} className="mb-20">
        <Grid item xs={12} sm={12}>
          <Card elevation={4}>
            {/* User Profile Photo */}
            <CardContent className="flex">
              <motion.div
                className={clsx(classes.avatarWrapper, 'w-96 h-96')}
                variants={photo}
                initial="hidden"
                animate="show">
                <Avatar
                  alt="profile photo"
                  className={clsx(classes.avatar, 'w-96 h-96 bg-gray-300')}
                  src={userPhotoSrc}
                />

                {/* Delete User Photo */}
                {user?.photo_url && (
                  <>
                    <Tooltip title="Fotoğrafı Sil" placement="bottom">
                      <div
                        className={clsx(
                          classes.deleteBtn,
                          'rounded-full justify-center items-center'
                        )}>
                        <IconButton
                          aria-label="delete_photo"
                          size="medium"
                          onClick={handleDeletePhoto}>
                          <DeleteForeverIcon color="primary" fontSize="large" />
                        </IconButton>

                        {loading && (
                          <CircularProgress size={68} color="primary" className="absolute z-10" />
                        )}
                      </div>
                    </Tooltip>
                  </>
                )}
              </motion.div>

              {/* User Info -> Name-Surname-Title */}
              <motion.div
                className="flex-1 flex flex-col justify-center items-start p-8 mx-12"
                variants={text}
                initial="hidden"
                animate="show">
                <Typography
                  className="text-14  md:text-18 font-semibold text-grey-700 break-all"
                  variant="h5">
                  {user?.name} {user?.surname}
                </Typography>
                <Chip
                  label={systemUserRole}
                  className="px-4 mt-10 font-semibold capitalize text-white bg-light-blue-500"
                />
              </motion.div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default SystemUserCard;
